import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, LogOut, Lock, RefreshCw, Inbox, Search } from "lucide-react";

const TOKEN_KEY = "primec2e_admin_token";

const LEAD_LABELS = {
  audit: { label: "Audit", cls: "bg-emerald/15 text-emerald border-emerald/40" },
  callback: { label: "Rappel", cls: "bg-brandblue/15 text-brandblue border-brandblue/40" },
  contact: { label: "Contact", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

const BUILDING = {
  bureau: "Bureaux", ecole: "Enseignement", hopital: "Santé", commerce: "Commerce",
  entrepot: "Entrepôt", hotel: "Hôtellerie",
};

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Connexion réussie");
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F8EC] px-6">
      <div className="w-full max-w-md bg-white border border-slate-200 shadow-xl p-8 lg:p-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-14 w-14 bg-white rounded-md p-1 border border-slate-200 flex items-center justify-center">
            <img src="/logo.jpg" alt="Logo PRIMEC2E" className="h-full w-full object-contain" />
          </span>
          <div>
            <p className="font-display font-black text-xl text-midnight">PRIMEC2E</p>
            <p className="text-xs uppercase tracking-widest text-slate-500">Espace administrateur</p>
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold text-midnight flex items-center gap-2">
          <Lock className="h-5 w-5 text-emerald" /> Connexion
        </h1>
        <p className="text-sm text-slate-500 mt-1">Accédez aux demandes reçues.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <Label htmlFor="admin-email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
            <Input id="admin-email" type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none mt-1.5" placeholder="admin@primec2e.info" />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-xs uppercase tracking-wider text-slate-500">Mot de passe</Label>
            <Input id="admin-password" type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none mt-1.5" placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full rounded-none bg-emerald hover:bg-emerald-600 text-white py-6 font-medium">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const { data } = await api.get("/admin/leads", { headers: { Authorization: `Bearer ${token}` } });
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expirée, reconnectez-vous");
        onLogout();
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const filtered = leads.filter((l) => {
    const okType = filter === "all" || l.lead_type === filter;
    const hay = `${l.name} ${l.company || ""} ${l.email} ${l.phone}`.toLowerCase();
    return okType && hay.includes(q.toLowerCase());
  });

  const counts = {
    all: leads.length,
    audit: leads.filter((l) => l.lead_type === "audit").length,
    callback: leads.filter((l) => l.lead_type === "callback").length,
    contact: leads.filter((l) => l.lead_type === "contact").length,
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#FAFCF5]">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 bg-white rounded-md p-0.5 border border-slate-200 flex items-center justify-center">
              <img src="/logo.jpg" alt="Logo PRIMEC2E" className="h-full w-full object-contain" />
            </span>
            <div>
              <p className="font-display font-black text-lg text-midnight leading-none">PRIMEC2E · Admin</p>
              <p className="text-xs text-slate-500 mt-1">Demandes reçues</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} data-testid="admin-refresh" className="rounded-none border-slate-300">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={onLogout} data-testid="admin-logout" className="rounded-none border-slate-300">
              <LogOut className="h-4 w-4 mr-2" /> Déconnexion
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-8">
          {[
            { k: "all", label: "Total demandes" },
            { k: "audit", label: "Audits" },
            { k: "callback", label: "Rappels" },
            { k: "contact", label: "Contacts" },
          ].map((s) => (
            <button
              key={s.k}
              onClick={() => setFilter(s.k)}
              data-testid={`admin-stat-${s.k}`}
              className={`bg-white p-6 text-left transition-colors ${filter === s.k ? "ring-2 ring-emerald ring-inset" : "hover:bg-slate-50"}`}
            >
              <div className="font-display text-3xl font-black text-midnight">{counts[s.k]}</div>
              <p className="text-sm text-slate-500 mt-1">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" placeholder="Rechercher (nom, société, email, téléphone)" className="rounded-none pl-9" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="rounded-none w-full sm:w-52" data-testid="admin-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les demandes</SelectItem>
              <SelectItem value="audit">Audits</SelectItem>
              <SelectItem value="callback">Rappels</SelectItem>
              <SelectItem value="contact">Contacts</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200" data-testid="admin-leads-table">
          {loading ? (
            <div className="p-16 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-16 flex flex-col items-center justify-center text-slate-400">
              <Inbox className="h-10 w-10 mb-3" />
              <p>Aucune demande pour le moment.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Société</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Bâtiment</TableHead>
                    <TableHead>Surface</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((l) => {
                    const t = LEAD_LABELS[l.lead_type] || LEAD_LABELS.contact;
                    return (
                      <TableRow key={l.id} data-testid={`admin-lead-row-${l.id}`}>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                        <TableCell><Badge className={`rounded-none border font-medium ${t.cls}`}>{t.label}</Badge></TableCell>
                        <TableCell className="font-medium text-midnight whitespace-nowrap">{l.name}</TableCell>
                        <TableCell className="text-slate-600 whitespace-nowrap">{l.company || "—"}</TableCell>
                        <TableCell className="text-sm">
                          <a href={`mailto:${l.email}`} className="text-emerald hover:underline block">{l.email}</a>
                          <a href={`tel:${l.phone}`} className="text-slate-500 block">{l.phone}</a>
                        </TableCell>
                        <TableCell className="text-slate-600 whitespace-nowrap">{BUILDING[l.building_type] || l.building_type || "—"}</TableCell>
                        <TableCell className="text-slate-600 whitespace-nowrap">{l.surface ? `${l.surface} m²` : "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs">{l.message || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false

  const check = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    try {
      await api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } });
      setAuthed(true);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Admin · PRIMEC2E";
    check();
  }, [check]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F4F8EC]"><Loader2 className="h-6 w-6 animate-spin text-emerald" /></div>;
  }
  return authed ? <Dashboard onLogout={logout} /> : <LoginView onSuccess={() => setAuthed(true)} />;
}
